import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './OnboardingModal.css';

const STEPS = [
  {
    icon: '✦',
    title: 'Welcome to AirFlyp',
    subtitle: 'Your AI-powered workspace',
    body: 'AirFlyp helps you manage clients, schedule sessions, and collaborate with your team — all in one place. Let\'s take a quick tour.',
    cta: 'Let\'s go →',
    tip: null,
  },
  {
    icon: '👤',
    title: 'Add your first client',
    subtitle: 'Step 1 of 3',
    body: 'Everything starts with a client. Add their name, email, and phone — then start logging session notes, transcriptions, and AI summaries.',
    cta: 'Got it →',
    tip: 'Tip: You can also let clients book themselves using your public booking link.',
    action: { label: 'Add first client', path: '/createclient' },
  },
  {
    icon: '✦',
    title: 'Meet your AI Persona',
    subtitle: 'Step 2 of 3',
    body: 'Your AI Persona is a virtual assistant that talks to clients in your voice. Train it with your tone and writing style — it handles intake, questions, and session summaries.',
    cta: 'Got it →',
    tip: 'Tip: The more writing samples you give it, the more it sounds like you.',
    action: { label: 'Set up AI Persona', path: '/persona' },
  },
  {
    icon: '✦✦',
    title: 'Collaborate with Circles',
    subtitle: 'Step 3 of 3',
    body: 'Have a team? Create a Circle to share clients, notes, and AI summaries with colleagues. Everyone stays in sync — no more copying notes between systems.',
    cta: 'Start exploring →',
    tip: 'Tip: Circles require a Circle plan. Your teammates join for free.',
    action: { label: 'Create a Circle', path: '/circles/create' },
  },
];

const OnboardingModal = ({ onComplete }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Small delay for smooth entrance
    setTimeout(() => setVisible(true), 100);
  }, []);

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  const handleNext = () => {
    if (isLast) {
      handleComplete();
    } else {
      setStep(s => s + 1);
    }
  };

  const handleComplete = () => {
    // Parent (MyDashboard) handles the localStorage flag
    setVisible(false);
    setTimeout(() => onComplete(), 300);
  };

  const handleAction = (path) => {
    handleComplete();
    navigate(path);
  };

  return (
    <div className={`onboarding-overlay ${visible ? 'visible' : ''}`}>
      <div className={`onboarding-modal ${visible ? 'visible' : ''}`}>

        {/* Progress dots */}
        <div className="onboarding-dots">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`onboarding-dot ${i === step ? 'active' : i < step ? 'done' : ''}`}
            />
          ))}
        </div>

        {/* Skip */}
        <button className="onboarding-skip" onClick={handleComplete}>
          Skip
        </button>

        {/* Icon */}
        <div className={`onboarding-icon ${step === 0 ? 'large' : ''}`}>
          {current.icon}
        </div>

        {/* Content */}
        {current.subtitle && step > 0 && (
          <p className="onboarding-subtitle">{current.subtitle}</p>
        )}
        <h2 className="onboarding-title">{current.title}</h2>
        <p className="onboarding-body">{current.body}</p>

        {current.tip && (
          <div className="onboarding-tip">
            💡 {current.tip}
          </div>
        )}

        {/* Actions */}
        <div className="onboarding-actions">
          {current.action && (
            <button
              className="onboarding-action-btn"
              onClick={() => handleAction(current.action.path)}
            >
              {current.action.label}
            </button>
          )}
          <button
            className={`onboarding-next-btn ${!current.action ? 'full' : ''}`}
            onClick={handleNext}
          >
            {current.cta}
          </button>
        </div>

        {/* Step counter */}
        {step > 0 && (
          <p className="onboarding-counter">{step} of {STEPS.length - 1}</p>
        )}

      </div>
    </div>
  );
};

export default OnboardingModal;
